import {
  getAuthToken,
  setAuthToken,
  removeAuthToken,
} from "@/lib/authUtils";

import axios from "axios";
import config from "../../../config/config";
import * as mocks from "./auth.mocks";
import { LoginResponse } from "./auth.interfaces";

const AUTH_BASE = `${config.apiBaseUrl}/auth`;

// Define JwtPayload interface
interface JwtPayload {
  role?: string;
  "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"?: string;
  [key: string]: any;
}

// Define a simple manual JWT decoder since jwt-decode is not installed
const manualJwtDecode = (token: string): JwtPayload => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return { role: '' };
  }
};

const API_URL = `${config.apiBaseUrl}/login`;

const axiosClient = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: config.defaultTimeout,
});

axiosClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const apiLogin = async (
  email: string,
  password: string
): Promise<LoginResponse> => {
  try {
    const response = await axiosClient.post<LoginResponse>(API_URL, {
      email,
      password,
    });

    if (response.status === 200) {
      return response.data;
    }
    throw new Error("Login failed");
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Axios error during login:", error.message);
    } else {
      console.error("Unexpected error during login:", error);
    }
    throw error;
  }
};

export const login = async (
  email: string,
  password: string
): Promise<LoginResponse> => {
  const response = await (config.useRealApi
    ? apiLogin(email, password)
    : mocks.login(email, password));

  setAuthToken(response.token);
  return response;
};

export const logout = () => {
  removeAuthToken();
};

export interface ConfirmEmailResponse {
  message: string;
}

export const confirmEmail = async (
  userId: string,
  token: string
): Promise<ConfirmEmailResponse> => {
  const response = await axios.get<ConfirmEmailResponse>(
    `${AUTH_BASE}/confirm-email`,
    { params: { userId, token }, timeout: config.defaultTimeout }
  );
  return response.data;
};

export interface ResendConfirmationEmailParams {
  userId?: string;
  email?: string;
}

/** Resend registration confirmation email. Provide userId (from confirm-email URL) or email. */
export const resendConfirmationEmail = async (
  params: ResendConfirmationEmailParams
): Promise<void> => {
  try {
    const response = await axios.post(
      `${AUTH_BASE}/resend-confirmation-email`,
      params,
      { timeout: config.defaultTimeout }
    );
    if (response.status !== 204 && response.status !== 200) {
      throw new Error((response.data as { message?: string })?.message ?? "Falha ao reenviar email.");
    }
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.data && typeof err.response.data === "object" && "message" in err.response.data) {
      throw new Error(String((err.response.data as { message: unknown }).message));
    }
    throw err;
  }
};

export const isAuthenticated = () => {
  const token = getAuthToken();
  return !!token;
};

export const isUserAdmin = () => {
 const token = getAuthToken();
 if (!token) return false;

 try {
   const decoded = manualJwtDecode(token);
   const role = decoded.role || decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
   return role === 'admin';
 } catch {
   return false;
 }
};

export const isUserStudent = () => {
 const token = getAuthToken();
 if (!token) return false;

 try {
   const decoded = manualJwtDecode(token);
   const role = decoded.role || decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
   return role === 'student';
 } catch {
   return false;
 }
};
